import { useEffect, useState } from "react";
import { useLocation } from "@/components/location-provider";
import { GlassPanel } from "@/components/ui/glass-panel";
import { PageHeader } from "@/components/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, Sun, Moon, Sunrise, Sunset, Compass } from "lucide-react";
import { SkyDome } from "@/components/astronomy/sky-dome";
import { TwilightBar } from "@/components/astronomy/twilight-bar";
import {
  MoonPhaseWeek,
  StargazingWeek,
} from "@/components/astronomy/moon-and-stargazing-week";
import { PlanetsTonight } from "@/components/astronomy/planets-tonight";
import { MeteorShowers } from "@/components/astronomy/meteor-showers";
import {
  solarPosition,
  lunarPosition,
  moonPhaseFromFraction,
} from "@/lib/astronomy";

interface DailyForecast {
  dt: number;
  moon_phase?: number;
  moonrise?: number;
  moonset?: number;
  sunrise?: number;
  sunset?: number;
  clouds?: number;
  weather?: { description: string }[];
}

interface CurrentWeather {
  dt: number;
  clouds?: number;
  visibility?: number;
  uvi?: number;
  dew_point?: number;
  pressure?: number;
  weather?: { description: string }[];
}

function formatTime(ts: number) {
  return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDuration(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function AstronomyPage() {
  const [current, setCurrent] = useState<CurrentWeather | null>(null);
  const [daily, setDaily] = useState<DailyForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const { current: location } = useLocation();

  useEffect(() => {
    setLoading(true);
    fetch(`/api/weather?lat=${location.lat}&lon=${location.lon}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.weather?.current) setCurrent(d.weather.current);
        if (d.weather?.daily) setDaily(d.weather.daily);
      })
      .catch(() => {
        setCurrent(null);
        setDaily([]);
      })
      .finally(() => setLoading(false));
  }, [location.lat, location.lon]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="space-y-5 animate-slide-up">
        <PageHeader
          eyebrow="Sky · Astronomy"
          title="Astronomy"
          subtitle={`${location.name}, ${location.country}`}
        />
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="space-y-5 animate-slide-up">
        <PageHeader
          eyebrow="Sky · Astronomy"
          title="Astronomy"
          subtitle={`${location.name}, ${location.country}`}
        />
        <GlassPanel intensity="low" className="p-12 text-center">
          <Star className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Astronomy data unavailable</p>
        </GlassPanel>
      </div>
    );
  }

  // Compute a few page-level summary values that the lib already produces.
  const sun = solarPosition(now, location.lat, location.lon);
  const moon = lunarPosition(now, location.lat, location.lon);
  const today = daily[0];
  const dayLength = today?.sunrise && today?.sunset
    ? formatDuration(today.sunset - today.sunrise)
    : "—";
  const nightLength = today?.sunrise && today?.sunset
    ? formatDuration(86400 - (today.sunset - today.sunrise))
    : "—";
  const moonPhase = moonPhaseFromFraction(moon.phaseAngleDeg / 360);
  const description = current.weather?.[0]?.description;

  return (
    <div className="space-y-5 animate-slide-up">
      <PageHeader
        eyebrow="Sky · Astronomy"
        title="Astronomy"
        subtitle={`${location.name}, ${location.country}`}
      />

      {/* HERO: live sky dome */}
      <SkyDome cloudPct={current.clouds ?? 0} description={description} />

      {/* Summary stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <GlassPanel intensity="low" className="p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 ring-1 ring-amber-500/20 flex-shrink-0">
            <Sun className="h-5 w-5 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Sun altitude
            </p>
            <p className="text-base font-bold tabular-nums leading-tight">
              {sun.altitudeDeg.toFixed(0)}°
            </p>
            <p className="text-[10px] text-muted-foreground">
              az {sun.azimuthDeg.toFixed(0)}°
            </p>
          </div>
        </GlassPanel>
        <GlassPanel intensity="low" className="p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-500/10 ring-1 ring-slate-500/20 flex-shrink-0">
            <span className="text-xl" aria-hidden>
              {moonPhase.emoji}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Moon
            </p>
            <p className="text-base font-bold leading-tight">{moonPhase.name}</p>
            <p className="text-[10px] text-muted-foreground tabular-nums">
              {Math.round(moon.illumination * 100)}% lit · age {moon.ageDays.toFixed(1)}d
            </p>
          </div>
        </GlassPanel>
        <GlassPanel intensity="low" className="p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 ring-1 ring-amber-500/20 flex-shrink-0">
            <Sunrise className="h-5 w-5 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Sun events
            </p>
            <p className="text-xs font-bold leading-tight tabular-nums">
              ↑ {today?.sunrise ? formatTime(today.sunrise) : "—"}
              <span className="text-muted-foreground font-normal mx-1">·</span>
              ↓ {today?.sunset ? formatTime(today.sunset) : "—"}
            </p>
            <p className="text-[10px] text-muted-foreground">
              Day {dayLength} · Night {nightLength}
            </p>
          </div>
        </GlassPanel>
        <GlassPanel intensity="low" className="p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/10 ring-1 ring-indigo-500/20 flex-shrink-0">
            <Compass className="h-5 w-5 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Moon events
            </p>
            <p className="text-xs font-bold leading-tight tabular-nums">
              ↑ {today?.moonrise ? formatTime(today.moonrise) : "—"}
              <span className="text-muted-foreground font-normal mx-1">·</span>
              ↓ {today?.moonset ? formatTime(today.moonset) : "—"}
            </p>
            <p className="text-[10px] text-muted-foreground">
              Distance {Math.round(moon.distanceKm).toLocaleString()} km
            </p>
          </div>
        </GlassPanel>
      </div>

      {/* Twilight phases bar */}
      <TwilightBar />

      {/* Detailed Sun + Moon cards */}
      <div className="grid gap-4 lg:grid-cols-2">
        <GlassPanel intensity="medium" className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/10">
              <Sun className="h-4 w-4 text-amber-400" />
            </div>
            <h3 className="text-sm font-semibold">Sun details</h3>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Stat label="Altitude" value={`${sun.altitudeDeg.toFixed(2)}°`} />
            <Stat label="Azimuth" value={`${sun.azimuthDeg.toFixed(1)}°`} />
            <Stat label="Declination" value={`${sun.declinationDeg.toFixed(2)}°`} />
            <Stat label="Hour angle" value={`${sun.hourAngleDeg.toFixed(1)}°`} />
            <Stat label="Sunrise" value={today?.sunrise ? formatTime(today.sunrise) : "—"} icon={<Sunrise className="h-3 w-3 text-amber-400" />} />
            <Stat label="Sunset" value={today?.sunset ? formatTime(today.sunset) : "—"} icon={<Sunset className="h-3 w-3 text-orange-400" />} />
            <Stat label="Day length" value={dayLength} />
            <Stat label="Night length" value={nightLength} />
            <Stat
              label="Solar noon"
              value={
                today?.sunrise && today?.sunset
                  ? formatTime((today.sunrise + today.sunset) / 2)
                  : "—"
              }
            />
            <Stat
              label="UV index now"
              value={current.uvi != null ? current.uvi.toFixed(1) : "—"}
            />
          </div>
        </GlassPanel>

        <GlassPanel intensity="medium" className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-500/10">
              <Moon className="h-4 w-4 text-slate-300" />
            </div>
            <h3 className="text-sm font-semibold">Moon details</h3>
          </div>
          <div className="text-center py-2 mb-3">
            <span className="text-5xl">{moonPhase.emoji}</span>
            <p className="text-sm font-bold mt-1">{moonPhase.name}</p>
            <p className="text-xs text-muted-foreground tabular-nums">
              {Math.round(moon.illumination * 100)}% illumination · age {moon.ageDays.toFixed(1)} days
            </p>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Stat label="Altitude" value={`${moon.altitudeDeg.toFixed(2)}°`} />
            <Stat label="Azimuth" value={`${moon.azimuthDeg.toFixed(1)}°`} />
            <Stat label="Distance" value={`${Math.round(moon.distanceKm).toLocaleString()} km`} />
            <Stat label="Phase angle" value={`${moon.phaseAngleDeg.toFixed(0)}°`} />
            <Stat label="Moonrise" value={today?.moonrise ? formatTime(today.moonrise) : "—"} />
            <Stat label="Moonset" value={today?.moonset ? formatTime(today.moonset) : "—"} />
          </div>
        </GlassPanel>
      </div>

      {/* 7-day moon strip */}
      <MoonPhaseWeek daily={daily} />

      {/* Stargazing forecast + planets side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        <StargazingWeek daily={daily} />
        <PlanetsTonight />
      </div>

      {/* Meteor showers */}
      <MeteorShowers />
    </div>
  );
}

function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold inline-flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="text-sm font-bold tabular-nums leading-tight">{value}</p>
    </div>
  );
}
